import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Checkbox, Button, Card, DarkSelect, Input, PageHeader } from "@/components/ui";

interface Segment {
  segment: string;
  count: number;
  label: string;
  description: string;
  group?: string;
}

interface SegmentsResponse {
  segments: Segment[];
}

interface DraftState {
  id: number;
  status:
    | "awaiting_content"
    | "ready"
    | "sending"
    | "done"
    | "canceled";
  segment: string | null;
  has_content: boolean;
  queued: number | null;
  sent_count: number | null;
  failed_count: number | null;
}

interface PickedUser {
  telegram_id: number;
  username: string | null;
}

const PRODUCT_RU: Record<string, string> = { any: "любой продукт", vpn: "VPN", proxy: "прокси" };

function promoHint(p: {
  kind?: string; value?: number; product?: string;
  ttl_hours?: number; max_uses: number | null; used_count: number;
  options?: string | null;
}): string {
  const kind = p.kind ?? "discount";
  let what: string;
  if (kind === "days") what = `+${p.value} дн ${PRODUCT_RU[p.product ?? "any"]}`;
  else if (kind === "topup_bonus") what = `+${p.value}% к пополнению`;
  else if (kind === "choice") {
    try {
      const opts = JSON.parse(p.options || "[]") as { kind: string; value: number; product?: string }[];
      what = "выбор: " + opts.map((o) =>
        o.kind === "days" ? `+${o.value}дн ${PRODUCT_RU[o.product ?? "any"]}`
        : o.kind === "topup_bonus" ? `+${o.value}% топап`
        : `−${o.value}%`).join(" / ");
    } catch { what = "выбор юзера"; }
  } else what = `скидка ${p.value}% · ${PRODUCT_RU[p.product ?? "any"]}`;
  const left = p.max_uses == null ? "без лимита"
    : `осталось ${Math.max(0, p.max_uses - p.used_count)} из ${p.max_uses}`;
  const ttl = kind === "days" ? "" : ` · сгорает через ${p.ttl_hours || 24} ч`;
  return `${what} · ${left}${ttl}`;
}

export default function Broadcast() {
  // ?segment=expiring:1 — приходит с дашборда («62 заканчиваются завтра»),
  // чтобы напоминание ушло именно этим людям, а не всем платникам.
  const [sp] = useSearchParams();
  const [segment] = useState(sp.get("segment") || "all");
  // Мультивыбор: несколько сегментов сразу + режим объединения. Пусто → одиночный
  // segment (обратная совместимость с дашборд-ссылками ?segment=...).
  const [multi, setMulti] = useState<Set<string>>(new Set());
  // Кого вычесть из аудитории. Отдельно от multi: роль сегмента задаётся явно,
  // порядок кликов ни на что не влияет (раньше «исключение» было режимом над
  // общим списком и базой молча становился первый выбранный ключ).
  const [excl, setExcl] = useState<Set<string>>(new Set());
  const [segMode, setSegMode] = useState<"union" | "intersect">("union");
  const [activeGroup, setActiveGroup] = useState<string>("");
  // Произвольный режим: рассылка точечно выбранным юзерам (для теста на себе /
  // адресной отправки). Эффективный сегмент = manual:<tid,tid,...>.
  const [manualMode, setManualMode] = useState(false);
  const [manualUsers, setManualUsers] = useState<PickedUser[]>([]);
  const [uq, setUq] = useState("");
  const [text, setText] = useState("");
  const [queued, setQueued] = useState<number | null>(null);
  const [sentId, setSentId] = useState<number | null>(null);
  const [canceledMsg, setCanceledMsg] = useState(false);
  const [mode, setMode] = useState<"text" | "bot">("text");
  const [btnText, setBtnText] = useState("");
  const [btnUrl, setBtnUrl] = useState("");
  const [btnAction, setBtnAction] = useState<
    "none" | "buy" | "vpn" | "renew" | "promo" | "url" | "tariff"
  >("none");
  const [btnPromo, setBtnPromo] = useState("");
  // «Конкретный тариф» (ТЗ п.3): продукт + тариф + опц. промокод.
  const [tfProduct, setTfProduct] = useState<"vpn" | "proxy">("vpn");
  const [tfPeriod, setTfPeriod] = useState("month");
  const [tfPromo, setTfPromo] = useState("");
  // Анти-двойная отправка быстрого текста: ключ стабилен для одного составления,
  // после успешной отправки перегенерируется.
  const [sendKey, setSendKey] = useState<string>(() =>
    (crypto.randomUUID?.() ?? String(Math.random())),
  );

  const botInfo = useQuery<{ username: string | null }>({
    queryKey: ["bot-info"],
    queryFn: () => api("/api/admin/bot-info"),
  });
  const promos = useQuery<{ items: {
    code: string; kind?: string; value?: number; product?: string;
    ttl_hours?: number; max_uses: number | null; used_count: number;
    options?: string | null;
  }[] }>({
    queryKey: ["promo-codes", "active"],
    queryFn: () => api("/api/admin/promo-codes?active_only=true&limit=100"),
    enabled: btnAction === "promo",
  });

  // Состояние rich-format draft через бота (новый флоу).
  const [draftId, setDraftId] = useState<number | null>(null);
  const [draftState, setDraftState] = useState<DraftState | null>(null);

  const extraSeg = sp.get("segment")?.startsWith("expiring:") ? sp.get("segment")! : "";
  const segments = useQuery<SegmentsResponse>({
    queryKey: ["broadcast", "segments", extraSeg],
    queryFn: () =>
      api(
        `/api/admin/broadcast/segments${extraSeg ? `?extra=${encodeURIComponent(extraSeg)}` : ""}`,
      ),
  });

  const send = useMutation({
    mutationFn: (body: {
      segment?: string | null;
      segments?: string[];
      segment_mode?: string;
      text: string;
      button_text?: string | null;
      button_url?: string | null;
      idempotency_key?: string;
      pin_message?: boolean;
    }) =>
      api<{ queued: number; id?: number }>("/api/admin/broadcast", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (res) => {
      setQueued(res.queued);
      setSentId(res.id ?? null);
      setCanceledMsg(false);
      setText("");
      setSendKey(crypto.randomUUID?.() ?? String(Math.random()));
    },
  });

  const startDraft = useMutation({
    mutationFn: (body: {
      segment?: string | null;
      segments?: string[];
      segment_mode?: string;
      button_text?: string | null;
      button_url?: string | null;
    }) =>
      api<DraftState>("/api/admin/broadcast/draft/start", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (res) => {
      setDraftId(res.id);
      setDraftState(res);
    },
  });

  const [pinAtUser, setPinAtUser] = useState(false);
  const confirmDraft = useMutation({
    mutationFn: (id: number) =>
      api<{ queued: number }>(
        `/api/admin/broadcast/draft/${id}/confirm${pinAtUser ? "?pin=true" : ""}`,
        { method: "POST" },
      ),
    onSuccess: (res) => {
      setQueued(res.queued);
    },
  });

  const cancelDraft = useMutation({
    mutationFn: (id: number) =>
      api<DraftState>(`/api/admin/broadcast/draft/${id}/cancel`, {
        method: "POST",
      }),
    // Не обнуляем: при остановке НА ЛЕТУ раннер ещё дописывает статус — держим
    // поллинг, пока не станет canceled (покажем итог).
    onSuccess: (res) => {
      setDraftState(res);
    },
  });

  // Отмена быстрой (quick-text) рассылки — тот же эндпоинт cancel по id.
  const cancelQuick = useMutation({
    mutationFn: (id: number) =>
      api(`/api/admin/broadcast/draft/${id}/cancel`, { method: "POST" }),
    onSuccess: () => setCanceledMsg(true),
  });

  // Поллинг draft state каждые 2 сек пока статус активен.
  useEffect(() => {
    if (!draftId) return;
    if (draftState?.status === "done" || draftState?.status === "canceled") {
      return;
    }
    const t = setInterval(async () => {
      try {
        const r = await api<DraftState>(
          `/api/admin/broadcast/draft/${draftId}`,
        );
        setDraftState(r);
      } catch {
        // sluggish API — пропускаем, следующий тик попробует снова
      }
    }, 2000);
    return () => clearInterval(t);
  }, [draftId, draftState?.status]);

  const list = segments.data?.segments ?? [];
  const selected = list.find((s) => s.segment === segment);
  // Группы-вкладки в порядке их первого появления в списке.
  const groups: string[] = [];
  for (const s of list) {
    const g = s.group || "Прочее";
    if (!groups.includes(g)) groups.push(g);
  }
  const curGroup = activeGroup || groups[0] || "";
  const groupList = list.filter((s) => (s.group || "Прочее") === curGroup);
  const multiCount = multi.size;
  const exclCount = excl.size;
  // Клик по чипу перебирает три состояния: не выбран → кому слать → исключить →
  // не выбран. Один сегмент не может быть одновременно в обоих списках.
  const toggleMulti = (key: string) => {
    setManualMode(false);
    const inSend = multi.has(key);
    const inExcl = excl.has(key);
    if (inSend) {
      setMulti((p) => { const n = new Set(p); n.delete(key); return n; });
      setExcl((p) => new Set(p).add(key));
    } else if (inExcl) {
      setExcl((p) => { const n = new Set(p); n.delete(key); return n; });
    } else {
      setMulti((p) => new Set(p).add(key));
    }
  };

  // Поиск юзеров для произвольного режима.
  const userSearch = useQuery<{ users: PickedUser[] }>({
    queryKey: ["bcast-user-search", uq.trim()],
    queryFn: () => api(`/api/admin/users/search?q=${encodeURIComponent(uq.trim())}`),
    enabled: manualMode && uq.trim().length >= 2,
  });
  const addUser = (u: PickedUser) => {
    setManualUsers((prev) => (prev.some((p) => p.telegram_id === u.telegram_id) ? prev : [...prev, u]));
    setUq("");
  };
  const effectiveSegment = manualMode
    ? `manual:${manualUsers.map((u) => u.telegram_id).join(",")}`
    : segment;
  const audienceReady = manualMode
    ? manualUsers.length > 0
    : multiCount > 0
      ? true
      : true;
  // Тело запроса по аудитории: мультивыбор → segments+mode; иначе одиночный.
  // exclude_segments вычитается всегда, в том числе из одиночной базы.
  const audienceBody = (): {
    segment?: string; segments?: string[]; segment_mode?: string;
    exclude_segments?: string[];
  } => {
    if (manualMode) return { segment: effectiveSegment };
    const ex = exclCount > 0 ? { exclude_segments: [...excl] } : {};
    if (multiCount > 0) return { segments: [...multi], segment_mode: segMode, ...ex };
    return { segment, ...ex };
  };
  // Точный охват считает сервер: сложить счётчики сегментов нельзя — люди в них
  // перекрываются, а пересечение/исключение вообще не выводятся из сумм.
  const reachQuery = useQuery<{ count: number }>({
    queryKey: ["broadcast", "reach", [...multi].sort(), [...excl].sort(), segMode, segment],
    enabled: !manualMode && (multiCount > 0 || exclCount > 0),
    queryFn: () =>
      api("/api/admin/broadcast/reach", {
        method: "POST",
        body: JSON.stringify(audienceBody()),
      }),
  });
  const multiReach = reachQuery.data?.count ?? 0;

  const PERIOD_LABELS: Record<string, string> = {
    week: "неделя", month: "месяц", "3month": "3 месяца",
    "6month": "6 месяцев", "12month": "год", forever: "навсегда",
  };
  function defaultBtnLabel(a: typeof btnAction): string {
    if (a === "tariff")
      return `💳 ${tfProduct === "vpn" ? "VPN" : "Proxy"} — ${PERIOD_LABELS[tfPeriod] || tfPeriod}`;
    return a === "buy"
      ? "💳 Оплатить"
      : a === "promo"
        ? "🎟 Активировать промокод"
        : a === "vpn"
          ? "🛡 Открыть VPN"
          : a === "renew"
            ? "🔁 Продлить"
            : "Открыть";
  }
  function buildBtnUrl(): string | null {
    const bot = botInfo.data?.username;
    if (btnAction === "none") return null;
    if (btnAction === "url") return btnUrl.trim() || null;
    if (!bot) return null;
    if (btnAction === "promo")
      return btnPromo ? `https://t.me/${bot}?start=promo_${btnPromo}` : null;
    if (btnAction === "tariff") {
      // buy_vpn_{period}[_{PROMO}] | buy_proxy_standard_{period}[_{PROMO}]
      const promo = tfPromo ? `_${tfPromo}` : "";
      const start = tfProduct === "vpn"
        ? `buy_vpn_${tfPeriod}${promo}`
        : `buy_proxy_standard_${tfPeriod}${promo}`;
      return `https://t.me/${bot}?start=${start}`;
    }
    return `https://t.me/${bot}?start=${btnAction}`;
  }
  function btnDisabled(): boolean {
    if (btnAction === "none") return false;
    return !buildBtnUrl();
  }

  const recipientCount = manualMode
    ? manualUsers.length
    : multiCount > 0 || exclCount > 0
      ? multiReach
      : selected?.count ?? 0;

  // Человекочитаемая аудитория: «кому» через знак режима, затем «− кого исключили».
  const audienceLabel = (() => {
    const base = multiCount > 0
      ? [...multi].join(segMode === "union" ? " ∪ " : " ∩ ")
      : segment;
    return exclCount > 0 ? `${base} − ${[...excl].join(" − ")}` : base;
  })();

  // Панель кнопки — общая для ОБОИХ режимов (объединение формат+кнопка).
  const buttonPanel = (
    <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
        Кнопка под сообщением (опционально)
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(
          [
            ["none", "Без кнопки"],
            ["buy", "💳 Оплатить"],
            ["promo", "🎟 Промокод"],
            ["vpn", "🛡 VPN"],
            ["renew", "🔁 Продлить"],
            ["tariff", "🎯 Конкретный тариф"],
            ["url", "🔗 Своя ссылка"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setBtnAction(k)}
            className={`px-3 py-1.5 rounded-lg text-xs transition ${
              btnAction === k
                ? "bg-brand-soft text-brand ring-1 ring-inset ring-brand/25 font-semibold"
                : "text-zinc-400 hover:bg-white/[0.04] bg-white/[0.02]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {btnAction !== "none" && (
        <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input
            placeholder={`Текст кнопки (по умолч. «${defaultBtnLabel(btnAction)}»)`}
            value={btnText}
            onChange={(e) => setBtnText(e.target.value)}
          />
          {btnAction === "url" && (
            <Input
              placeholder="https://… или t.me/…"
              value={btnUrl}
              onChange={(e) => setBtnUrl(e.target.value)}
              className="font-mono"
            />
          )}
          {btnAction === "promo" && (
            <div className="w-56">
              <DarkSelect
                value={btnPromo}
                onChange={setBtnPromo}
                placeholder="— выбери промокод —"
                options={(promos.data?.items ?? []).map((p) => ({
                  value: p.code,
                  label: p.code,
                  hint: promoHint(p),
                }))}
              />
            </div>
          )}
          {btnAction === "tariff" && (
            <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
              {(["vpn", "proxy"] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setTfProduct(p)}
                  className={`px-3 py-1.5 rounded-lg text-xs transition ${
                    tfProduct === p
                      ? "bg-brand-soft text-brand ring-1 ring-inset ring-brand/25 font-semibold"
                      : "text-zinc-400 bg-white/[0.02] hover:bg-white/[0.04]"
                  }`}
                >
                  {p === "vpn" ? "🛡 VPN" : "🌐 Proxy"}
                </button>
              ))}
              <div className="w-40">
                <DarkSelect
                  value={tfPeriod}
                  onChange={setTfPeriod}
                  options={Object.entries(PERIOD_LABELS)
                    // VPN не имеет дневного тарифа; week — минимум VPN
                    .filter(([k]) => tfProduct === "vpn" || k !== "week")
                    .filter(([k]) => tfProduct === "proxy" || k !== "day")
                    .map(([k, label]) => ({ value: k, label }))}
                />
              </div>
              <div className="w-52">
                <DarkSelect
                  value={tfPromo}
                  onChange={setTfPromo}
                  placeholder="— промокод (необязательно) —"
                  options={[{ value: "", label: "— без промокода —" }].concat(
                    (promos.data?.items ?? []).map((p) => ({
                      value: p.code, label: p.code, hint: promoHint(p),
                    })) as any
                  )}
                />
              </div>
            </div>
          )}
        </div>
      )}
      {btnAction !== "none" && btnAction !== "url" && (
        <div className="text-[11px] text-zinc-600 mt-1.5 font-mono break-all">
          {buildBtnUrl() ?? "…"}
        </div>
      )}
      {btnAction === "promo" && !botInfo.data?.username && (
        <div className="text-[11px] text-amber-400 mt-1">
          не удалось получить @бота — ссылка не соберётся
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <PageHeader title="Рассылка" />

      {/* Шаг 1 — аудитория */}
      <div className="mb-5">
        <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-2">
          Аудитория
        </div>
        {/* Вкладки-группы */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {groups.map((g) => {
            const on = g === curGroup && !manualMode;
            const inGroup = list.filter((s) => (s.group || "Прочее") === g)
              .filter((s) => multi.has(s.segment)).length;
            return (
              <button
                key={g}
                onClick={() => { setManualMode(false); setActiveGroup(g); }}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                  on ? "bg-white/[0.06] border-white/20 text-zinc-100"
                     : "border-white/[0.05] text-zinc-500 hover:text-zinc-300 hover:border-white/[0.12]"
                }`}
              >
                {g}
                {inGroup > 0 && (
                  <span className="ml-1.5 text-brand font-semibold">{inGroup}</span>
                )}
              </button>
            );
          })}
          <button
            onClick={() => setManualMode(true)}
            title="Точечно выбранным юзерам — для теста на себе или адресной отправки"
            className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
              manualMode ? "bg-white/[0.06] border-white/20 text-zinc-100"
                         : "border-white/[0.05] text-zinc-500 hover:text-zinc-300 hover:border-white/[0.12]"
            }`}
          >
            🎯 Произвольно
            {manualUsers.length > 0 && (
              <span className="ml-1.5 text-brand font-semibold">{manualUsers.length}</span>
            )}
          </button>
        </div>

        {/* Чипы сегментов текущей группы — мультивыбор (клик = вкл/выкл) */}
        {!manualMode && (
          <div className="flex flex-wrap gap-2">
            {groupList.map((s) => {
              const checked = multi.has(s.segment);
              const excluded = excl.has(s.segment);
              // Одиночный режим (multi пуст): подсветить активный single-сегмент.
              const single = multiCount === 0 && !excluded && segment === s.segment;
              const active = checked || single;
              return (
                <button
                  key={s.segment}
                  onClick={() => toggleMulti(s.segment)}
                  title={`${s.description}\n\nКлик: выбрать → исключить → снять`}
                  className={`flex items-center gap-2.5 px-3.5 py-2 rounded-xl border transition-all ${
                    excluded
                      ? "bg-rose-500/10 border-rose-500/40"
                      : active
                        ? "bg-brand-soft border-brand/40 glow-ring"
                        : "border-white/[0.07] hover:border-white/[0.16] hover:bg-white/[0.02]"
                  }`}
                >
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center text-[9px] ${
                    excluded ? "bg-rose-500 border-rose-500 text-black"
                             : checked ? "bg-brand border-brand text-black"
                                       : "border-white/20 text-transparent"
                  }`}>{excluded ? "−" : "✓"}</span>
                  <span className={`text-sm ${
                    excluded ? "text-rose-300 font-semibold line-through decoration-rose-500/50"
                             : active ? "text-brand font-semibold" : "text-zinc-200"
                  }`}>
                    {s.label}
                  </span>
                  <span
                    className={`text-xs font-mono tabular-nums px-1.5 py-0.5 rounded-md ${
                      excluded ? "bg-rose-500/20 text-rose-300"
                               : active ? "bg-brand/20 text-brand" : "bg-white/[0.05] text-zinc-400"
                    }`}
                  >
                    {s.count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Сводка аудитории: кому шлём, кого вычитаем, точный охват с сервера.
            Режим применяется ТОЛЬКО к «кому слать» — исключения вычитаются всегда. */}
        {!manualMode && (multiCount > 0 || exclCount > 0) && (
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {multiCount > 1 && (
              <div className="flex gap-1">
                {([
                  ["union", "Любой из выбранных ∪"],
                  ["intersect", "Все условия сразу ∩"],
                ] as const).map(([m, lbl]) => (
                  <button
                    key={m}
                    onClick={() => setSegMode(m)}
                    className={`text-xs px-2.5 py-1 rounded-lg border transition-all ${
                      segMode === m ? "bg-white/[0.06] border-white/20 text-zinc-100"
                                    : "border-white/[0.05] text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            )}
            <span className="text-xs text-zinc-500">
              {multiCount > 0
                ? `кому: ${multiCount}`
                : `кому: все (${segment === "all" ? "вся база" : segment})`}
              {exclCount > 0 && (
                <span className="text-rose-400"> · минус {exclCount}</span>
              )}
              {" · получат "}
              <span className="text-zinc-200 font-semibold tabular-nums">
                {reachQuery.isFetching ? "…" : multiReach}
              </span>
            </span>
            <button
              onClick={() => { setMulti(new Set()); setExcl(new Set()); }}
              className="text-xs text-zinc-600 hover:text-zinc-400 underline"
            >
              сбросить
            </button>
          </div>
        )}
        {!manualMode && multiCount === 0 && selected?.description && (
          <div className="text-xs text-zinc-600 mt-2">{selected.description}</div>
        )}

        {manualMode && (
          <div className="mt-3 rounded-xl border border-white/[0.07] p-3 max-w-2xl">
            <div className="text-xs text-zinc-500 mb-2">
              Рассылка уйдёт только выбранным. Найдите себя, чтобы протестировать формат перед массовой отправкой.
            </div>
            <div className="relative">
              <Input value={uq} onChange={(e) => setUq(e.target.value)} placeholder="@username или telegram_id" />
              {uq.trim().length >= 2 && (
                <div className="absolute z-30 mt-1 w-full rounded-lg border border-white/[0.1] max-h-56 overflow-auto shadow-pop" style={{ backgroundColor: "#0c0e12" }}>
                  {userSearch.isLoading && <div className="px-3 py-2 text-xs text-zinc-500">Ищу…</div>}
                  {!userSearch.isLoading && (userSearch.data?.users ?? []).length === 0 && (
                    <div className="px-3 py-2 text-xs text-zinc-500">Не нашёл</div>
                  )}
                  {(userSearch.data?.users ?? []).map((u) => (
                    <button
                      key={u.telegram_id}
                      onClick={() => addUser(u)}
                      className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-white/[0.05]"
                    >
                      {u.username ? `@${u.username}` : `#${u.telegram_id}`}
                      <span className="text-xs text-zinc-500 font-mono ml-2">{u.telegram_id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {manualUsers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {manualUsers.map((u) => (
                  <span key={u.telegram_id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/[0.05] text-xs text-zinc-200">
                    {u.username ? `@${u.username}` : `#${u.telegram_id}`}
                    <button
                      onClick={() => setManualUsers((prev) => prev.filter((p) => p.telegram_id !== u.telegram_id))}
                      className="text-zinc-500 hover:text-rose-400"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Шаг 2 — способ */}
      <div className="inline-flex items-center gap-1 mb-4 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06]">
        {([
          ["text", "✍️ Быстрый текст"],
          ["bot", "🤖 Через бота"],
        ] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setMode(k)}
            className={`h-9 px-4 rounded-lg text-sm font-medium transition-all ${
              mode === k ? "bg-brand-soft text-brand glow-ring" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "text" && (
        <Card>
          <textarea
            className="w-full min-h-[220px] p-3.5 rounded-xl bg-black/25 border border-white/[0.08] text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/20 transition"
            placeholder="Что написать… (обычный текст, без форматирования)"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />

          {buttonPanel}

          <div className="mt-3">
            <Checkbox
              checked={pinAtUser}
              onChange={setPinAtUser}
              label="📌 Закрепить сообщение у юзеров (тихо)"
            />
          </div>

          <div className="flex items-center gap-3 flex-wrap mt-3">
            <Button
              disabled={!text.trim() || send.isPending || btnDisabled() || !audienceReady}
              onClick={() => {
                const url = buildBtnUrl();
                send.mutate({
                  ...audienceBody(),
                  text,
                  button_text: url ? btnText.trim() || defaultBtnLabel(btnAction) : null,
                  button_url: url,
                  idempotency_key: sendKey,
                  pin_message: pinAtUser,
                });
              }}
            >
              {send.isPending ? "Отправляю…" : `Отправить → ${recipientCount} получателей`}
            </Button>
            {send.isError && (
              <span className="text-red-400 text-sm">{(send.error as Error).message}</span>
            )}
            {queued !== null && !send.isError && !draftId && (
              <span className="flex items-center gap-3 text-sm">
                <span className="text-emerald-400">
                  {canceledMsg
                    ? `Остановлено. Отправку прервали (ушло ≤ ${queued}).`
                    : `Ушло в рассылку: ${queued}. Идёт в фоне.`}
                </span>
                {sentId !== null && !canceledMsg && (
                  <button
                    onClick={() => cancelQuick.mutate(sentId)}
                    disabled={cancelQuick.isPending}
                    className="text-xs px-2.5 py-1 rounded-lg border border-red-400/30 text-red-400 hover:bg-red-400/10 disabled:opacity-50"
                  >
                    {cancelQuick.isPending ? "Останавливаю…" : "⛔ Отменить рассылку"}
                  </button>
                )}
              </span>
            )}
          </div>
        </Card>
      )}

      {mode === "bot" && (
        <Card title="Через бота — форматирование, эмодзи, премиум-эмодзи">
          {!draftId && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-400">
                Этот режим переносит сообщение из вашего Telegram-клиента
                байт-в-байт. Поддерживается всё что умеет TG: жирный,
                курсив, подчёркнутый, зачёркнутый, моноширинный,
                цитаты, спойлеры, ссылки, обычные эмодзи и
                премиум-эмодзи.
              </p>
              <p className="text-sm text-zinc-400">
                Аудитория из выбранного выше:{" "}
                <code className="text-zinc-300">{manualMode
                  ? `произвольно (${manualUsers.length})`
                  : audienceLabel}</code> ·{" "}
                <b>{recipientCount}</b> получателей.
              </p>
              {buttonPanel}
              <Button
                disabled={startDraft.isPending || !audienceReady || btnDisabled()}
                onClick={() => {
                  const url = buildBtnUrl();
                  startDraft.mutate({
                    ...audienceBody(),
                    button_text: url ? btnText.trim() || defaultBtnLabel(btnAction) : null,
                    button_url: url,
                  });
                }}
              >
                {startDraft.isPending
                  ? "Запускаю…"
                  : "Создать рассылку → перейти в бота"}
              </Button>
              {startDraft.isError && (
                <div className="text-red-400 text-sm">
                  {(startDraft.error as Error).message}
                </div>
              )}
            </div>
          )}

          {draftId && draftState && (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="text-zinc-500 uppercase tracking-wider mr-2">
                  draft #{draftState.id}
                </span>
                <StatusPill status={draftState.status} />
                {draftState.segment && (
                  <span className="ml-2 text-zinc-400">
                    сегмент:{" "}
                    <code className="text-zinc-300">{draftState.segment}</code>
                  </span>
                )}
              </div>

              {draftState.status === "awaiting_content" && (
                <div className="text-sm text-amber-300">
                  ⏳ Ждём ваше сообщение в личке бота. Откройте его в
                  Telegram, наберите/отформатируйте сообщение и пришлите.
                  Бот ответит «✅ Сообщение получено» — это поле обновится
                  само в течение 2 секунд.
                </div>
              )}

              {draftState.status === "ready" && (
                <>
                  <div className="text-sm text-emerald-400">
                    ✅ Сообщение получено и сохранено. Превью у вас в
                    личке бота. Подтвердите рассылку.
                  </div>
                  <Checkbox
                    checked={pinAtUser}
                    onChange={setPinAtUser}
                    label="📌 Закрепить сообщение у юзеров (тихо)"
                  />
                  <div className="flex gap-3 flex-wrap">
                    <Button
                      disabled={confirmDraft.isPending}
                      onClick={() => confirmDraft.mutate(draftState.id)}
                    >
                      {confirmDraft.isPending
                        ? "Отправляю…"
                        : `Подтвердить и разослать (${draftState.queued ?? recipientCount})`}
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={cancelDraft.isPending}
                      onClick={() => cancelDraft.mutate(draftState.id)}
                    >
                      Отменить
                    </Button>
                  </div>
                </>
              )}

              {draftState.status === "sending" && (
                <div className="space-y-2">
                  <div className="text-sm text-blue-300">
                    🚚 Рассылка идёт… можно закрыть страницу.
                  </div>
                  <div className="text-xs text-zinc-400 tabular-nums">
                    Отправлено: <b>{draftState.sent_count ?? 0}</b> · Ошибок:{" "}
                    <b>{draftState.failed_count ?? 0}</b>
                    {draftState.queued ? ` из ${draftState.queued}` : ""}
                  </div>
                  <Button
                    variant="ghost"
                    disabled={cancelDraft.isPending}
                    onClick={() => cancelDraft.mutate(draftState.id)}
                  >
                    ⏹ Остановить
                  </Button>
                </div>
              )}

              {draftState.status === "done" && (
                <div className="space-y-1">
                  <div className="text-sm text-emerald-400">
                    ✅ Рассылка завершена.
                  </div>
                  <div className="text-xs text-zinc-400">
                    Отправлено: <b>{draftState.sent_count ?? 0}</b> ·
                    Ошибок: <b>{draftState.failed_count ?? 0}</b>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDraftId(null);
                      setDraftState(null);
                    }}
                  >
                    Создать новую
                  </Button>
                </div>
              )}

              {draftState.status === "canceled" && (
                <div className="space-y-1">
                  <div className="text-sm text-zinc-400">
                    Рассылка отменена.
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDraftId(null);
                      setDraftState(null);
                    }}
                  >
                    Создать новую
                  </Button>
                </div>
              )}

              {confirmDraft.isError && (
                <div className="text-red-400 text-sm">
                  {(confirmDraft.error as Error).message}
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: DraftState["status"] }) {
  const map: Record<DraftState["status"], { label: string; cls: string }> = {
    awaiting_content: {
      label: "ждём сообщение",
      cls: "bg-amber-900/40 text-amber-300 border-amber-700/50",
    },
    ready: {
      label: "готово к отправке",
      cls: "bg-emerald-900/40 text-emerald-300 border-emerald-700/50",
    },
    sending: {
      label: "рассылка идёт",
      cls: "bg-blue-900/40 text-blue-300 border-blue-700/50",
    },
    done: {
      label: "готово",
      cls: "bg-zinc-800 text-zinc-300 border-zinc-700",
    },
    canceled: {
      label: "отменена",
      cls: "bg-zinc-900 text-zinc-500 border-zinc-800",
    },
  };
  const s = map[status];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${s.cls}`}
    >
      {s.label}
    </span>
  );
}
